import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectIsIndexDateAV,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  getProjectCohortVariableViewPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import {
  type AnalyticalVariableCategory,
  deriveCategoryFromAnalyticalVariable,
  formatBoldText,
} from '@verantos-dev/feature-analytical-variables';
import { CriteriaSelection } from '@verantos-dev/types-cohorts';

import { AVPreviewModalRenderer } from '../../../../components/AVPreviewModalRenderer/AVPreviewModalRenderer';
import {
  CriteriaViewCard,
  CriteriaViewCardProps,
} from '../../../../components/CriteriaViewCard/CriteriaViewCard';
import {
  CriteriaCardType,
  CriteriaStatusType,
} from '../../../../components/CriteriaViewCard/types';
import { useIndexDateSelectionText } from '../../../../hooks/useIndexDateSelectionText';
import { getIndexDateSelection } from '../../../../utils/indexDateSelection';
import type { CriterionRow } from '../useCriteriaSection';
import { useCriterionCard } from './useCriterionCard';

const CATEGORY_TO_CARD_TYPE: Record<AnalyticalVariableCategory, CriteriaCardType> = {
  clinical: CriteriaCardType.CLINICAL,
  demographic: CriteriaCardType.DEMOGRAPHIC,
  observability: CriteriaCardType.OBSERVABILITY,
};

interface CriterionCardProps {
  row: CriterionRow;
  /** Forwarded to the shared card — present only when this criterion is
   *  currently draggable (see `CriteriaSection`). */
  dragHandleProps?: CriteriaViewCardProps['dragHandleProps'];
}

/**
 * Gen-3 criterion card — thin wrapper around the v0 `CriteriaViewCard`
 * shared component. The v0 card carries every visual atom the
 * Definition tab needs (chips, change-status badge, edit/delete
 * buttons, divider, hover affordance). Gen-3 adds nothing new
 * visually; it just builds the props from Zustand-shaped data and
 * pure handlers (no Redux, no RTK).
 *
 * Description rendering: passed in as a `details` ReactNode so we can
 * format markdown (`**bold**` on entity names) inline. The v0 card
 * accepts arbitrary ReactNode for detail values, so the markdown
 * renders inside the existing card chrome without any v0 changes.
 */
export const CriterionCard = ({ row, dragHandleProps }: CriterionCardProps) => {
  const { criterion, analyticalVariable, libraryItem, index } = row;
  const intl = useIntl();
  const { projectId, cohortId } = useParams<{ projectId: string; cohortId: string }>();
  const {
    isOwner,
    canDelete,
    actionsDisabled,
    actionsDisabledReason,
    handleDelete,
    isPreviewModalOpen,
    handlePreview,
    handleClosePreviewModal,
    conceptGroups,
    cohortOwner,
    cohortLastModified,
  } = useCriterionCard(row);

  // The "Index date" badge appears whenever a criterion *is* the index
  // date — including AV criteria flagged as index date in the store.
  const isIndexDate = useCohortDefinitionStore((s) =>
    analyticalVariable ? selectIsIndexDateAV(analyticalVariable.id)(s) : false
  );

  // Fall back to CLINICAL when the AV is missing or has no structured logic
  // tree (SQL-only AVs from the de novo / agent path). Structured library
  // refs derive their category from the embedded jsonValue when present.
  const cardType: CriteriaCardType | null = (() => {
    if (!analyticalVariable) return CriteriaCardType.CLINICAL;
    if (libraryItem?.kind === 'av_denovo') return null;
    const category = deriveCategoryFromAnalyticalVariable(analyticalVariable);
    return category ? CATEGORY_TO_CARD_TYPE[category] : CriteriaCardType.CLINICAL;
  })();

  // Stub criterion = no analytical variable resolved yet. Emitted by the
  // cohort_drafter (OR-6037) and held in the cohort version's criteria
  // array until the per-stub resolve loop attaches a real AV. Render as
  // a read-only placeholder with the stub's plain-language description
  // as the title and a ``PENDING`` chip alongside the role chip.
  // OR-6071: a fixed calendar-date index is resolved on arrival — the date
  // IS the index, there's no AV to attach. It must NOT render as a pending
  // stub even though it has no analyticalVariable.
  const calendarDateValue =
    criterion.criteriaType === 'index_date' && criterion.value.type === 'Calendar date'
      ? criterion.value.indexDateValue
      : undefined;
  const isStub = !analyticalVariable && !calendarDateValue;
  // OR-6077: the backend owns the criterion title via `name` (set to the
  // drafter's description on a stub). Read it instead of synthesizing the
  // title from `description` on the frontend.
  const stubName =
    criterion.criteriaType === 'analytical_variable'
      ? criterion.name ?? ''
      : criterion.criteriaType === 'index_date' && criterion.value.type === 'Analytical variable'
      ? criterion.value.name ?? ''
      : '';

  const statusChips: CriteriaStatusType[] = useMemo(() => {
    const chips: CriteriaStatusType[] = [];
    if (criterion.criteriaType === 'index_date' || isIndexDate) {
      chips.push(CriteriaStatusType.INDEX_DATE);
    }
    if (criterion.criteriaType === 'analytical_variable') {
      switch (criterion.criteriaSelection) {
        case CriteriaSelection.INCLUSION:
          chips.push(CriteriaStatusType.INCLUSION);
          break;
        case CriteriaSelection.EXCLUSION:
          chips.push(CriteriaStatusType.EXCLUSION);
          break;
        case CriteriaSelection.NOT_APPLIED:
          chips.push(CriteriaStatusType.NOT_APPLIED);
          break;
        default:
          break;
      }
    }
    if (isStub) {
      chips.push(CriteriaStatusType.PENDING);
    }
    return chips;
  }, [criterion, isIndexDate, isStub]);

  // Boolean index dates show how the date is picked: occurrence (first / last /
  // nth) and absolute vs. all-other-criteria-met. Calendar-date and stub index
  // criteria have no resolution.
  // TODO: cleanup with ra_conditional_index_date ff
  const isConditionalIndexDateEnabled = useFeatureFlagSplit('ra_conditional_index_date');
  const formatIndexDateSelection = useIndexDateSelectionText();
  const indexDateSelection =
    isConditionalIndexDateEnabled &&
    criterion.criteriaType === 'index_date' &&
    criterion.value.type === 'Analytical variable'
      ? formatIndexDateSelection(getIndexDateSelection(criterion.value.dateResolution))
      : undefined;

  // Render `**bold**` markers as inline <span> via the shared
  // formatBoldText helper. ReactMarkdown produces block-level <p>/<div>
  // which nest illegally inside the CriteriaViewCard's <Typography>
  // detail-row (renders as <p>) — see the DOM nesting warnings under
  // the previous markdown render.
  const descriptionNode = analyticalVariable?.description
    ? formatBoldText(analyticalVariable.description)
    : calendarDateValue
    ? intl.formatMessage({ id: 'projects.cohortBuilder.criterionCard.fixedCalendarIndexDate' })
    : isStub
    ? // Placeholder copy for unresolved stubs per the M4 workspace design.
      intl.formatMessage({ id: 'projects.cohortBuilder.criterionCard.definitionPending' })
    : '';

  // Stub criteria show the backend-provided `name` (the drafter's title for
  // the unresolved slot). Resolved criteria show the bound AV's name. The
  // frontend reads the backend field rather than deciding the title itself
  // (OR-6077); `—` covers pre-migration versions where `name` is absent.
  let title = '—';
  if (libraryItem?.dataElement) {
    title = libraryItem.dataElement;
  } else if (analyticalVariable?.name) {
    title = analyticalVariable.name;
  } else if (calendarDateValue) {
    title = `Index date: ${calendarDateValue}`;
  } else if (isStub && stubName) {
    title = stubName;
  }
  const serialNumber = String(index).padStart(2, '0');

  // changeStatus is carried on the AV for AV criteria, on the criterion
  // itself for index_date criteria. Fall through cleanly when neither
  // path applies.
  const changeStatus =
    criterion.criteriaType === 'index_date'
      ? criterion.changeStatus ?? undefined
      : analyticalVariable?.changeStatus ?? undefined;

  const previewId = `av-preview-${analyticalVariable?.id ?? row.key}`;

  // ``libraryItem`` carries the canonical UUID for both av_ir and
  // av_denovo library refs. For cohort-local AVs (legacy clone-on-add)
  // we fall back to the IR stub's id. The renderer needs the UUID to
  // drive the av_denovo modal's lazy detail fetch.
  const previewAvId = libraryItem?.analyticalVariableId ?? analyticalVariable?.id;
  // av_denovo "View full" navigates to the cohort-scoped view route so
  // the URL itself carries the "from inside cohort X" context. The
  // breadcrumb hook parses ``cohortId`` from the path and renders the
  // cohort crumb automatically — no state sidecar needed, deep links
  // / back-forward / cmd-click-new-tab all preserve the context.
  // Cohort-local AVs (no libraryItem) fall back to the project-scoped
  // view since they don't have a stable library UUID.
  const viewFullHref =
    libraryItem?.kind === 'av_denovo' && projectId && previewAvId
      ? cohortId
        ? getProjectCohortVariableViewPath(projectId, cohortId, previewAvId)
        : getProjectVariablePath(projectId, previewAvId)
      : undefined;

  return (
    <>
      <CriteriaViewCard
        id={analyticalVariable?.id ?? row.key}
        serialNumber={serialNumber}
        cardType={cardType}
        title={title}
        statusChips={statusChips}
        indexDateSelection={indexDateSelection}
        details={[
          {
            key: 'description',
            value: descriptionNode,
          },
        ]}
        hideReorderButtons
        // No edit affordance on cohort criterion cards — inline AV editing
        // from the cohort is not a platform capability; changes go through
        // the Research Assistant. Delete exists only for the cohort owner
        // (non-owners get no icon at all); while locked (OR-6078: agent
        // working / assembling / processing / final / AD-referenced) the
        // owner's delete renders disabled with the lock reason as tooltip.
        canDelete={canDelete}
        actionsDisabled={actionsDisabled}
        actionTooltip={actionsDisabledReason}
        onDelete={isOwner ? handleDelete : undefined}
        onClick={analyticalVariable ? handlePreview : undefined}
        changeStatus={changeStatus}
        dragHandleProps={dragHandleProps}
      />
      <AVPreviewModalRenderer
        id={previewId}
        isOpen={isPreviewModalOpen}
        analyticalVariable={analyticalVariable}
        analyticalVariableId={previewAvId}
        libraryKind={libraryItem?.kind}
        viewFullHref={viewFullHref}
        conceptGroups={conceptGroups}
        onClose={handleClosePreviewModal}
        actionsDisabled={actionsDisabled}
        owner={cohortOwner}
        lastModified={cohortLastModified}
      />
    </>
  );
};
